/**
 * The vocabulary the backend stores product codes in.
 *
 * A stored key is the real code with a '5' inserted at index 3, so it is always one
 * digit longer than the code it stands for. The 2026-08-31 production survey
 * (.scratch/fact-families-persistence/evidence-2026-08-27-distribution-c-key.md,
 * "EAN wire-format survey") read every source the portal touches -- medpdb2.EAN,
 * portal_cache.c_key, portal_distribution.c_key, and the frontend-written
 * portal_comparison items.products / include / exclude columns -- and found the marker
 * present in ~100 percent of rows in every length bucket, against a ~10 percent chance
 * rate. The mask is therefore the contract, not a possibility: an ACL encodes one form
 * on the way out and requires that form on the way in.
 *
 * KNOWN LIMIT: the marker is necessary, not sufficient. A real code that happens to
 * carry a '5' at index 3 is indistinguishable from a stored key, and fromMasked will
 * unmask it into a different code. That residual ambiguity lives in the data format --
 * nothing in a row says which form it holds -- and cannot be resolved here.
 */
import { eanMask } from '../../../shared/utils/ean.util';
import { type EAN, tryEAN } from './ean';

/** A product code in the backend's stored form: the real code, masked. */
export type MaskedEan = string & { readonly __brand: 'MaskedEan' };

const MASK_MARKER = '5';
const MASK_MARKER_INDEX = 3;
const DIGIT_RUN = /^\d+$/;

/** The single key form a real code is stored under. */
export function toMasked(ean: EAN): MaskedEan {
  return eanMask(ean) as MaskedEan;
}

/**
 * The real code a stored key stands for, or null when the key is not one.
 *
 * Requires the mask marker: a bare-digit key without it is a contract violation, not a
 * code that is "already real", and returning it unchanged would be exactly the silent
 * fallback this module exists to remove. Callers report the null and skip the row.
 */
export function fromMasked(key: string): EAN | null {
  if (!DIGIT_RUN.test(key)) return null;
  if (key[MASK_MARKER_INDEX] !== MASK_MARKER) return null;
  return tryEAN(key.slice(4, 6) + key.slice(0, 3) + key.slice(6));
}
